//core
import React, {useCallback, useContext, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import { Scrollbars } from "react-custom-scrollbars";
import { useLocalStorage } from "usehooks-ts";
import arePropsEqual from "react-fast-compare";
import {
  Box,
  useMediaQuery,
} from "@mui/material";

//styles
import { useStyles } from "./styles";

//hooks
import useDidMountEffect from "../../../../../../../customHooks/useDidMountEffect";
import { useFields } from "../../../../../../../store/common/fields/useFields";
import { useSubAffDealsV2 } from "../../../../../../../store/admin/subAffDealsV2/useSubAffDealsV2";

//context
import { GlobalContext } from "../../../../../../../context/GlobalContext";

//constants
import { favoriteFilterKeys } from "../../../../constants";

//components
import FavoriteCheckboxField from "../../../../../../UI/Fields/FavoriteCheckboxField";
import { ISubAffDealsV2FilterDataType } from "../../../../types";
import MuiAutocompleteSelectLazy from "../../../../../../UI/Fields/MuiAutocompleteSelectLazy";
import Button from "../../../../../../UI/Buttons/Button";


interface IFilterProps {
  initialFilter: ISubAffDealsV2FilterDataType;
  activeField: string;
  setUpdateFilter: (data: any) => void;
  setToggleDrawer: (data: boolean) => void;
  setActiveField: (field: string) => void;
}

const Filter: React.FC<IFilterProps> = ({
  initialFilter,
  activeField,
  setUpdateFilter,
  setToggleDrawer,
  setActiveField,
}): JSX.Element => {
  const { t } = useTranslation();
  const styles = useStyles();
  const mobile = useMediaQuery('(max-width:767px)');

  const context = useContext(GlobalContext);

	const { getPlatforms } = useFields();
	const { getFilterCreator } = useSubAffDealsV2();

  const [filter, setFilter] = useState<ISubAffDealsV2FilterDataType>(context.admSubAffDealsV2Filter);
	const [platformsList, setPlatformsList] = useState<any | null>(null);

  //favorite fields state
	const [favAdmSADPlatformIds, setFavAdmSADPlatformIds] = useLocalStorage<boolean>(favoriteFilterKeys.platformIds, false);
	const [favAdmSADCreatedByIds, setFavAdmSADCreatedByIds] = useLocalStorage<boolean>(favoriteFilterKeys.createdByIds, false);

  useDidMountEffect(() => {
    context.setAdmSubAffDealsV2Filter(filter);
  }, [filter]);

	useEffect( () => {
		if (!platformsList) {
			(async () => {
				const data = await getPlatforms({});
				setPlatformsList(data.payload.list)
			})()
		}
	}, [platformsList]);

  const handleApplyFilter = useCallback(() => {
    setUpdateFilter(filter);
    setToggleDrawer(false);
  }, [filter]);

	const fieldPayload = () => {
		return {
			page: 1,
			search: null,
			per_page: 100,
		}
	};

  return (
    <>
      <Box className={styles.wrapper}>
        <Scrollbars
          className={styles.customScroll}
          style={{ overflow: "hidden" }}
          hideTracksWhenNotNeeded={true}
          renderView={props => <div {...props} className="view"/>}
          renderTrackHorizontal={props => <div {...props} className="track-horizontal"/>}
          renderTrackVertical={props => <div {...props} className="track-vertical"/>}
        >
          {/*createdByIds*/}
          <Box className={styles.box}>
	          <Box className={styles.fieldWrapper}>
		          {!mobile && (
			          <Box className={styles.fieldFavorite}>
				          <FavoriteCheckboxField
					          checked={favAdmSADCreatedByIds}
					          setChecked={setFavAdmSADCreatedByIds}
					          fieldName={t(`admin.deals.filters.created_by`)}
				          />
			          </Box>
		          )}
		          <Box className={styles.field}>
			          <MuiAutocompleteSelectLazy
				          keyValue="value"
				          keyId="id"
				          id="createdByIds"
				          name="createdByIds"
				          label={t(`admin.deals.filters.created_by`)}
				          value={filter.createdByIds}
				          multiple={true}
				          showSelectAll={false}
				          disabled={false}
				          getOptions={getFilterCreator}
				          payloadData={fieldPayload()}
				          labelWithId={false}
				          onChange={setFilter}
				          disableCloseOnSelect={true}
				          autoFocus={activeField === 'createdByIds'}
				          showLabelCounter={true}
			          />
		          </Box>
	          </Box>
          </Box>
	        {!!platformsList && platformsList.length > 1 && (
		        <Box className={styles.box}>
			        <Box className={styles.fieldWrapper}>
				        {!mobile && (
					        <Box className={styles.fieldFavorite}>
						        <FavoriteCheckboxField
							        checked={favAdmSADPlatformIds}
							        setChecked={setFavAdmSADPlatformIds}
							        fieldName={t(`admin.reports.filter.platformIds`)}
						        />
					        </Box>
				        )}
				        <Box className={styles.field}>
					        <MuiAutocompleteSelectLazy
						        keyValue="value"
						        keyId="id"
						        id="platformIds"
						        name="platformIds"
						        label={t(`admin.reports.filter.platformIds`)}
						        value={filter.platformIds}
						        multiple={true}
						        showSelectAll={false}
						        disabled={false}
						        getOptions={getPlatforms}
						        payloadData={fieldPayload()}
						        labelWithId={false}
						        onChange={setFilter}
						        disableCloseOnSelect={true}
						        autoFocus={activeField === 'platformIds'}
						        showLabelCounter={true}
					        />
				        </Box>
			        </Box>
		        </Box>
	        )}
        </Scrollbars>
      </Box>

      <Box className={styles.actions}>
        <Box className={styles.actionsWrapper}>
          <Button
            gradient
            className={styles.button}
            fullWidth
            disableElevation
            type="submit"
            variant="contained"
            size="large"
            onClick={handleApplyFilter}
            disabled={arePropsEqual(initialFilter, filter)}
          >
            {t("common.buttons.apply")}
          </Button>
          <Button
            className={styles.button}
            color="secondary"
            disableElevation
            type="button"
            variant="outlined"
            size="large"
            onClick={() => setToggleDrawer(false)}
          >
            {t("common.buttons.cancel")}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default Filter;
